import classNames from 'classnames';
import { differenceInSeconds, isFuture } from 'date-fns';
import React, { useCallback, useMemo } from 'react';

import * as AppPropTypes from '../../../lib/PropTypes';

import ResultsQuestion from './ResultsQuestion';

import styles from '../../../../styles/themes/rhoneacademy/results-rhoneacademy.module.scss';

const propTypes = {
    page: AppPropTypes.page.isRequired,
};

function ResultsRhoneacademy({ page }) {
    const { title: pageTitle = null, games = null, participations = null } = page;

    const [selectedGameIndex, setSelectedGameIndex] = React.useState(0);
    const [selectedQuestionsGroupIndex, setSelectedQuestionsGroupIndex] = React.useState(0);

    const { id: selectedGameId = null, questionsGroups = [] } = useMemo(
        () => (games || [])[selectedGameIndex] || null,
        [games, selectedGameIndex],
    );

    const participationForGame = useMemo(() => {
        const sortedParticipations = [...(participations || [])].sort(
            ({ updatedAt: a }, { updatedAt: b }) => new Date(b) - new Date(a),
        );
        return sortedParticipations.find(({ gameId }) => gameId === selectedGameId);
    }, [participations, selectedGameId]);

    const questions = useMemo(() => {
        const { questions: questionsOfGroup = [] } = questionsGroups[selectedQuestionsGroupIndex];
        return questionsOfGroup.map((question) => {
            const { id: questionId = null, duration: questionTimeLimit = null } = question;
            const { answers: participationAnswersForGame = null } = participationForGame || {};

            const {
                valid = null,
                validated = null,
                points = null,
                createdAt: answerCreatedAtStr = null,
                validatedAt: answerValidatedAtStr = null,
            } = (participationAnswersForGame || []).find(
                ({ question: answerQuestionId }) => answerQuestionId === questionId,
            ) || {};

            const answerCreatedAt =
                answerCreatedAtStr !== null ? new Date(answerCreatedAtStr) : null;
            const answerValidatedAt =
                answerValidatedAtStr !== null ? new Date(answerValidatedAtStr) : null;
            const answerDuration =
                answerCreatedAt !== null && answerValidatedAt !== null
                    ? differenceInSeconds(answerValidatedAt, answerCreatedAt)
                    : null;

            return {
                ...question,
                valid,
                validated,
                points,
                answerDuration: answerDuration !== null ? answerDuration : questionTimeLimit,
            };
        });
    }, [games, participations, selectedGameIndex, selectedQuestionsGroupIndex]);

    const correctQuestionsCount = useMemo(
        () =>
            questionsGroups.reduce(
                (acc, { questions: questionsOfGroup = [] }) =>
                    acc + questionsOfGroup.filter((c) => c.valid).length,
                0,
            ),
        [selectedGameIndex],
    );

    const totalQuestionsCount = useMemo(
        () =>
            questionsGroups.reduce(
                (acc, { questions: questionsOfGroup = [] }) => acc + questionsOfGroup.length,
                0,
            ),
        [selectedGameIndex],
    );

    const onTabClicked = useCallback(
        (index) => {
            setSelectedGameIndex(index);
            setSelectedQuestionsGroupIndex(0);
        },
        [setSelectedGameIndex, setSelectedQuestionsGroupIndex],
    );

    return (
        <div className={classNames(styles.container, 'noHeader')}>
            <div className={styles.header}>
                <div className={styles.circle}>
                    {correctQuestionsCount.toString().padStart(2, '0')}/
                    {totalQuestionsCount.toString().padStart(2, '0')}
                </div>
            </div>
            <div className={styles.tabs}>
                {(games || []).map(({ id, title, startAt: gameStartAt = null }, index) => {
                    const disabled = gameStartAt !== null && isFuture(new Date(gameStartAt));
                    return (
                        <button
                            key={id}
                            type="button"
                            className={classNames(styles.tab, {
                                [styles.active]: index === selectedGameIndex,
                            })}
                            onClick={() => onTabClicked(index)}
                            disabled={disabled}
                        >
                            {title}
                        </button>
                    );
                })}
            </div>
            <div className={styles.inner}>
                <h1>{pageTitle}</h1>
                <div className={styles.tabs}>
                    {questionsGroups.map(
                        ({ id, label, startAt: questionGroupStartAt = null }, index) => {
                            const disabled =
                                questionGroupStartAt !== null &&
                                isFuture(new Date(questionGroupStartAt));
                            return (
                                <button
                                    key={`${selectedGameId || ''}_${id}`}
                                    type="button"
                                    className={classNames(styles.tab, {
                                        [styles.active]: index === selectedQuestionsGroupIndex,
                                    })}
                                    onClick={() => setSelectedQuestionsGroupIndex(index)}
                                    disabled={disabled}
                                >
                                    {label}
                                </button>
                            );
                        },
                    )}
                </div>
                <div className={styles.questions}>
                    {questions.map((question) => (
                        <ResultsQuestion key={question.id} question={question} />
                    ))}
                </div>
            </div>
        </div>
    );
}

ResultsRhoneacademy.propTypes = propTypes;

export default ResultsRhoneacademy;
